import { db } from '../../../../config/firebase'
import { NODE_TYPE } from './catalogTaxonomyConstants'
import {
  buildSiblingReorderUpdates,
  countSubtreeByType,
} from './catalogTaxonomySubtree'
import { collectCatalogTaxonomyStoragePaths } from './catalogTaxonomyStorageCleanup'

export const MAX_SAFE_BATCH_WRITES = 450

/**
 * Products that directly assign any node in the subtree (catalogTaxonomyAssignmentIds).
 */
export async function collectProductAssignmentBlockers(nodes = []) {
  const blockers = []
  const nodeList = Array.isArray(nodes) ? nodes.filter((n) => n?.id) : []
  if (!nodeList.length) return blockers

  const linkedProductIds = new Set()
  const linkedByNode = []

  await Promise.all(
    nodeList.map(async (node) => {
      const snap = await db
        .collection('products')
        .where('catalogTaxonomyAssignmentIds', 'array-contains', node.id)
        .get()

      if (snap.empty) return

      const productIds = []
      snap.forEach((doc) => {
        linkedProductIds.add(doc.id)
        productIds.push(doc.id)
      })
      linkedByNode.push({ nodeId: node.id, name: node.name || '—', count: productIds.length })
    })
  )

  if (linkedProductIds.size > 0) {
    const sample = Array.from(linkedProductIds).slice(0, 5).join(', ')
    blockers.push({
      nodeId: nodeList[0].id,
      name: nodeList[0].name || '—',
      type: nodeList[0].type,
      code: 'has_product_assignments',
      message: `Є товари з призначенням на цю гілку (${linkedProductIds.size}). Спочатку змініть assignments. Приклади: ${sample}`,
      productCount: linkedProductIds.size,
      linkedByNode,
    })
  }

  return blockers
}

export const LEAF_PARENT_CHILD_BLOCK_MESSAGE =
  'Ця категорія вже призначена товарам як кінцева. Спочатку перенесіть товари до дочірніх категорій.'

/**
 * Block creating/restoring a non-archived child under a parent that is still a leaf
 * and already has direct product assignments.
 */
export async function assertCanAddNonArchivedChildUnderParent({
  parentId,
  siblingNodes = [],
  restoringNodeId = null,
}) {
  if (!parentId) return { ok: true, productCount: 0 }

  const otherNonArchived = (siblingNodes || []).filter(
    (node) =>
      node?.id &&
      node.id !== restoringNodeId &&
      node.parentId === parentId &&
      node.isArchived !== true
  )

  // Parent already has non-archived children → already non-leaf; no new transition.
  if (otherNonArchived.length > 0) {
    return { ok: true, productCount: 0 }
  }

  const snap = await db
    .collection('products')
    .where('catalogTaxonomyAssignmentIds', 'array-contains', parentId)
    .get()

  if (snap.empty) {
    return { ok: true, productCount: 0 }
  }

  return {
    ok: false,
    productCount: snap.size,
    message: `${LEAF_PARENT_CHILD_BLOCK_MESSAGE} (товарів: ${snap.size})`,
  }
}

/**
 * Publication + product assignment blockers.
 */
export async function collectSubtreeBlockers(nodes = []) {
  const blockers = []

  nodes.forEach((node) => {
    if (node?.firstPublishedAt != null) {
      blockers.push({
        nodeId: node.id,
        name: node.name || '—',
        type: node.type,
        code: 'was_published',
        message: 'Вузол або нащадок уже публікувався (firstPublishedAt). Доступне лише архівування.',
      })
    }
  })

  const productBlockers = await collectProductAssignmentBlockers(nodes)
  blockers.push(...productBlockers)

  return blockers
}

/**
 * Pure assembly of preflight from already-loaded subtree + siblings of root.
 */
export function assembleDeletePreflight({
  root,
  subtreeNodes,
  siblingsOfRoot = [],
  blockers = [],
}) {
  if (!root?.id) {
    return {
      ok: false,
      root: null,
      nodes: [],
      counts: countSubtreeByType([]),
      siblingReorder: { parentId: null, updates: [], updateCount: 0 },
      batch: {
        subtreeDeleteCount: 0,
        siblingSortOrderUpdateCount: 0,
        totalWrites: 0,
        maxSafeWrites: MAX_SAFE_BATCH_WRITES,
        fitsSingleBatch: false,
      },
      blockers: [
        {
          nodeId: '',
          name: '',
          type: '',
          code: 'other',
          message: 'Немає вузла для видалення.',
        },
      ],
      storagePathsPreview: [],
    }
  }

  const nodes = subtreeNodes?.length ? subtreeNodes : [root]
  const counts = countSubtreeByType(nodes)
  const parentId = root.type === NODE_TYPE.GLOBAL ? null : root.parentId ?? null
  const updates = buildSiblingReorderUpdates(siblingsOfRoot, root.id)
  const subtreeDeleteCount = nodes.length
  const siblingSortOrderUpdateCount = updates.length
  const totalWrites = subtreeDeleteCount + siblingSortOrderUpdateCount
  const fitsSingleBatch = totalWrites <= MAX_SAFE_BATCH_WRITES

  const nextBlockers = [...blockers]
  if (!fitsSingleBatch) {
    nextBlockers.push({
      nodeId: root.id,
      name: root.name || '—',
      type: root.type,
      code: 'batch_too_large',
      message: `Гілка завелика для безпечного видалення з BackOffice (${totalWrites} записів у batch, ліміт ${MAX_SAFE_BATCH_WRITES}).`,
    })
  }

  return {
    ok: nextBlockers.length === 0 && fitsSingleBatch,
    root: { id: root.id, type: root.type, name: root.name || '—' },
    nodes,
    counts,
    siblingReorder: {
      parentId,
      updates,
      updateCount: siblingSortOrderUpdateCount,
    },
    batch: {
      subtreeDeleteCount,
      siblingSortOrderUpdateCount,
      totalWrites,
      maxSafeWrites: MAX_SAFE_BATCH_WRITES,
      fitsSingleBatch,
    },
    blockers: nextBlockers,
    storagePathsPreview: collectCatalogTaxonomyStoragePaths(nodes),
  }
}
